import { isImmutable, fromJS } from 'immutable';

import {
  findPropertiesByType,
  populateMissingSchemaValues,
} from '@pearsontechnology/aero-plugin-schema';

import { composeState } from 'utils/scope/composeState';
import { resolveAssetUrnsInState } from 'services/coursewareItem/stateHelpers';

import { ACTIVITY, COMPONENT, INTERACTIVE } from '../../../services/coursewareItem/coursewareTypes';
import { STATE } from '../constants';


const allowedElementTypes = [ACTIVITY, COMPONENT, INTERACTIVE];

// This function adds "state" to the inputed courseware map using data from inputed walkable.
// Note: the input courseware map is assumed to be one containing ids of the input walkable, its components, and its pathway children
export const enhanceElementMapWithState = async (elementMap, walkable) => {
  let enhancedElementMap = fromJS({});

  if (!walkable) {
    throw new Error('failed to enhanceElementMapWithState, walkable undefined');
  }

  await Promise.allSettled(
    Object.entries(elementMap).map(async ([elementId, element]) => {
      try {
        if (!allowedElementTypes.includes(element.elementType)) {
          throw Error(`elementType ${element.elementType} unsupported for elementId ${elementId}`);
        }
        let accumlativeElementObject;

        // Convert all asset urns into urls
        accumlativeElementObject = await resolveAssets(element, elementMap);

        // Genrate state by using assets, schema, config and state from courseware and scope from walkable
        accumlativeElementObject = generateState(accumlativeElementObject, walkable);

        // Resolve Pathways if courseware is ACTIVITY
        if (
          accumlativeElementObject?.elementType && accumlativeElementObject.elementType === ACTIVITY
        ) {
          accumlativeElementObject = resolvePathways(accumlativeElementObject);
        }

        // and enhanced courseware object back into the courseware map
        enhancedElementMap = enhancedElementMap.setIn([elementId], accumlativeElementObject);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`cannot generate state for element: ${element?.id} due to ${e}`);
      }
    }),
  );

  return enhancedElementMap.toJS();
};

export const resolveAssets = async (courseware, elementMap) => {
  const {
    assets, schema, state, config, id, ancestry, elementType,
  } = courseware;

  if (!assets || !schema || !config) {
    // There are many instances where the courseware data isn't complete but a partial, i.e missing some fields
    // To resolve assets, we need to know assets, schema and config. Without these, skip asset resolve altogether
    return courseware;
  }


  // If the courseware is a component, look for assets stored in the parent walkable too
  let allAssets = [...assets];
  if (elementType === COMPONENT) {
    const ancestorWalkableId = ancestry[0]?.elementId;

    if (ancestorWalkableId) {
      const ancestoryWalkable = elementMap[ancestorWalkableId];
      allAssets = [...assets, ...ancestoryWalkable.assets];
    }
  }

  let coursewareImmutable = fromJS(courseware);

  const rawState = state || config;
  try {
    const resolvedState = await resolveAssetUrnsInState(allAssets, schema, rawState);
    coursewareImmutable = coursewareImmutable.setIn([STATE], resolvedState);
    return coursewareImmutable.toJS();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('unable to resolve asset for courseware', id);
  }

  return courseware;
};

export const generateState = (courseware, walkable) => {
  const { schema, config, assets, state, id: courseawereId } = courseware;
  const { scope } = walkable;

  if (!schema || !config || !scope) {
    // There are many instances where the courseware data isn't complete but a partial, i.e missing some fields
    // To generateState, we need to know schema, scope and config. Without these, skip generateState
    return courseware;
  }

  const configEnchanced = populateMissingSchemaValues(schema, fromJS(config), []);

  const generatedState = composeState(
    schema,
    state || configEnchanced,
    findElementDataInScope(courseawereId, scope).scopeData,
    assets,
  );

  let coursewareImmutable = fromJS(courseware);
  coursewareImmutable = coursewareImmutable.setIn(
    [STATE],
    isImmutable(generatedState) ? generatedState.toJS() : generatedState,
  );
  return coursewareImmutable.toJS();
};

export const resolvePathways = (accumlativeCourseware) => {
  const { schema, pathways, config } = accumlativeCourseware;
  if (!schema || !pathways || !config) {
    return accumlativeCourseware;
  }

  const pathwayProperties = findPropertiesByType(schema, 'pathway');
  if (!pathwayProperties || pathwayProperties.length === 0) {
    return accumlativeCourseware;
  }
  const immutableCourseware = fromJS(accumlativeCourseware);
  const resolvedState = pathwayProperties.reduce((acc, item) => {
    const { path } = item;
    const pathwayId = acc.getIn([STATE, ...path, 'pathwayId']);
    const pathway = pathways.find(({ id }) => id === pathwayId);
    const { walkables } = pathway || {};
    const replacementItems = walkables?.map(({ id }) => ({ itemId: id })) || [];
    return acc.setIn([STATE, ...path], replacementItems);
  }, immutableCourseware);
  return resolvedState.toJS();
};

export function findElementDataInScope(elementId, scope) {
  const elementScope = scope.find((item) => item.sourceId === elementId);
  if (!elementScope || !elementScope.data) {
    return { scopeURN: undefined, scopeData: undefined };
  }
  return {
    scopeURN: elementScope.scopeURN,
    scopeData: JSON.parse(elementScope.data),
  };
}
